import { useCallback, useEffect, useRef, useState } from 'react';
import PricingCard from '../home/PricingSection/PricingCard';

const VISIBLE_PLAN_COUNT = 3;
const MIN_CARD_WIDTH = 280;

function getVisiblePlanCount(containerWidth) {
  if (containerWidth < 720) return 1;
  if (containerWidth < 1100) return 2;
  return VISIBLE_PLAN_COUNT;
}

const LAYOUT = {
  default: { gap: 24, cardHeight: 545, navSize: 'h-10 w-10', chevron: 'h-5 w-5', padY: 'py-6', padX: 'px-3' },
  compact: { gap: 16, cardHeight: 450, navSize: 'h-9 w-9', chevron: 'h-4 w-4', padY: 'py-5', padX: 'px-2' },
};

function NavButton({ active, onClick, label, children, sizeClass }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!active}
      aria-label={label}
      aria-disabled={!active}
      className={`flex items-center justify-center rounded-full border-2 transition ${sizeClass} ${
        active
          ? 'border-[#9369CC] bg-white text-[#9369CC] shadow-md hover:bg-[#9369CC]/10'
          : 'cursor-default border-gray-300 bg-white/60 text-gray-300'
      }`}
    >
      {children}
    </button>
  );
}

function getCardWidth(containerWidth, gap, visibleCount) {
  const count = visibleCount || getVisiblePlanCount(containerWidth);
  return Math.max(
    MIN_CARD_WIDTH,
    Math.floor((containerWidth - gap * (count - 1)) / count)
  );
}

export default function PricingPlansCarousel({
  plans,
  loading,
  startPayment,
  payingPlanId,
  isPlanSubscribed,
  className = 'mt-6',
  emptyMessage = 'No subscription plans available right now.',
  compact = false,
}) {
  const layout = compact ? LAYOUT.compact : LAYOUT.default;
  const { gap: planGap, cardHeight, navSize, chevron: chevronSize, padY, padX } = layout;

  const scrollRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [cardWidth, setCardWidth] = useState(0);
  const [visibleCount, setVisibleCount] = useState(VISIBLE_PLAN_COUNT);

  const showNav = plans.length > visibleCount;
  const [fitsContainer, setFitsContainer] = useState(true);

  const measureLayout = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nextVisibleCount = getVisiblePlanCount(el.clientWidth);
    setVisibleCount(nextVisibleCount);
    setCardWidth(getCardWidth(el.clientWidth, planGap, nextVisibleCount));
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    setCanScrollPrev(scrollLeft > 4);
    setCanScrollNext(scrollLeft < maxScroll - 4);
    setFitsContainer(scrollWidth <= clientWidth + 4);
  }, [planGap]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
    const timer = window.setTimeout(measureLayout, 50);
    return () => window.clearTimeout(timer);
  }, [plans, measureLayout]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    measureLayout();
    const observer = new ResizeObserver(measureLayout);
    observer.observe(el);
    window.addEventListener('resize', measureLayout);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measureLayout);
    };
  }, [measureLayout, plans]);

  const scrollPlans = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    if (direction === 'prev' && !canScrollPrev) return;
    if (direction === 'next' && !canScrollNext) return;

    const step = (cardWidth || getCardWidth(el.clientWidth, planGap, visibleCount)) + planGap;

    el.scrollBy({
      left: direction === 'next' ? step : -step,
      behavior: 'smooth',
    });
  };

  const cardStyle = cardWidth
    ? { width: cardWidth, minWidth: cardWidth, maxWidth: cardWidth, height: cardHeight, flex: '0 0 auto' }
    : { height: cardHeight, flex: '0 0 auto' };

  const gapClass = compact ? 'gap-4' : 'gap-6';

  const chevronLeft = (
    <svg className={chevronSize} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );

  const chevronRight = (
    <svg className={chevronSize} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );

  if (loading) {
    return (
      <div className={`w-full min-w-0 overflow-visible ${className}`}>
        <div
          ref={scrollRef}
          className={`flex ${gapClass} overflow-x-auto overflow-y-visible ${padX} ${padY} [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
        >
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              data-plan-card
              style={cardStyle}
              className="animate-pulse rounded-2xl bg-white/70"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!plans.length) {
    return <p className={`text-center text-base font-medium text-[#666666] ${className}`}>{emptyMessage}</p>;
  }

  return (
    <div className={`relative w-full min-w-0 overflow-visible ${className}`}>
      {showNav && (
        <div className="mb-3 flex justify-center gap-2 sm:justify-end">
          <NavButton
            active={canScrollPrev}
            onClick={() => scrollPlans('prev')}
            label="Previous plans"
            sizeClass={navSize}
          >
            {chevronLeft}
          </NavButton>
          <NavButton
            active={canScrollNext}
            onClick={() => scrollPlans('next')}
            label="Next plans"
            sizeClass={navSize}
          >
            {chevronRight}
          </NavButton>
        </div>
      )}

      <div className="w-full min-w-0 overflow-visible">
        <div
          ref={scrollRef}
          onScroll={measureLayout}
          className={`flex w-full min-w-0 ${gapClass} overflow-x-auto overflow-y-visible ${padX} ${padY} [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
            fitsContainer && !showNav ? 'justify-center' : ''
          } ${plans.length > visibleCount ? 'snap-x snap-mandatory' : ''}`}
        >
          {plans.map((plan) => (
            <div
              key={plan.id}
              data-plan-card
              style={cardStyle}
              className={`flex min-w-0 overflow-visible ${plans.length > visibleCount ? 'snap-start' : ''}`}
            >
              <PricingCard
                plan={plan}
                compact={compact}
                onStartLearning={startPayment}
                paying={payingPlanId === Number(plan.id)}
                isSubscribed={isPlanSubscribed(plan)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
